import { Link } from '@tanstack/react-router';
import { MarketingShell } from '@/components/marketing/marketing-shell';
import { SeoHead, organizationJsonLd, breadcrumbJsonLd } from '@/components/marketing/seo-head';
import { BLOG_PILLARS, formatBlogDate, getAllBlogPosts } from '@/lib/blog';
import { BRAND } from '@maintenance-tracker/shared';
import { ArrowRight } from 'lucide-react';

export function BlogIndexPage() {
  const posts = getAllBlogPosts();

  return (
    <MarketingShell>
      <SeoHead
        title={`Blog — ${BRAND.name}`}
        description="Guides on maintenance agreement renewals, customer retention benchmarks, win-back campaigns, and tools for HVAC, plumbing, and service businesses."
        canonicalPath="/blog"
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
          ]),
        ]}
      />

      <div className="mx-auto max-w-4xl px-4 py-16">
        <p className="text-[13px] font-semibold uppercase tracking-wider text-brand">Resources</p>
        <h1 className="mt-2 text-4xl font-semibold tracking-tight">Maintenance agreement renewal guides</h1>
        <p className="mt-4 max-w-2xl text-lg text-text-secondary">
          Practical playbooks and benchmarks for service shop owners — structured so you can act this week, not just read.
        </p>

        <div className="mt-10 flex flex-wrap gap-2">
          {BLOG_PILLARS.map((p) => (
            <span
              key={p.id}
              className="rounded-full border border-border bg-bg-surface px-3 py-1 text-[13px] text-text-secondary"
            >
              {p.label}
            </span>
          ))}
        </div>

        <div className="mt-12 space-y-6">
          {posts.map((post) => (
            <article
              key={post.meta.slug}
              className="rounded-2xl border border-border bg-bg-surface p-6 shadow-card transition-shadow hover:shadow-modal"
            >
              <div className="flex flex-wrap items-center gap-2 text-[13px] text-text-muted">
                <span className="capitalize">{post.meta.pillar.replace('-', ' ')}</span>
                <span>·</span>
                <time dateTime={post.meta.published}>{formatBlogDate(post.meta.published)}</time>
              </div>
              <h2 className="mt-2 text-xl font-semibold tracking-tight">
                <Link to="/blog/$slug" params={{ slug: post.meta.slug }} className="hover:text-brand">
                  {post.meta.title}
                </Link>
              </h2>
              <p className="mt-2 text-text-secondary">{post.meta.description}</p>
              <Link
                to="/blog/$slug"
                params={{ slug: post.meta.slug }}
                className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
              >
                Read article <ArrowRight className="h-4 w-4" />
              </Link>
            </article>
          ))}
        </div>
      </div>
    </MarketingShell>
  );
}
